import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useReducer,
  useCallback,
} from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import AgoraRTM from "agora-rtm-sdk";
import { Button, Col, Container, Row, Toast } from "react-bootstrap";
import {
  FaUserCircle,
  FaVideo,
  FaVideoSlash,
  FaMicrophone,
  FaMicrophoneSlash,
  FaVolumeUp,
  FaUserSlash,
} from "react-icons/fa";
import { BiVolumeMute } from "react-icons/bi";
import {
  BsFullscreen,
  BsFullscreenExit,
  BsChevronCompactLeft,
  BsChevronCompactRight,
} from "react-icons/bs";
import { IoIosArrowRoundBack } from "react-icons/io";
import { IconContext } from "react-icons";
import { navigate } from "@reach/router";
import { Scrollbars } from "react-custom-scrollbars";
import { FullScreen, useFullScreenHandle } from "react-full-screen";

import BigSpinner from "components/BigSpinner";
import { useAppContext } from "libs/contextLib";

import "./live-stream.scss";

import {
  TEACH_DOMAIN_NO_ACTIVE_USER,
  LEARN_DOMAIN_NO_ACTIVE_USER,
  TEACHER_VIDEO_OFF,
  USER_WENT_OFFLINE,
  USER_VIDEO_OFF,
  YOUR_VIDEO_OFF,
  EXTERNALLY_MUTED,
  ANONYMOUS,
} from "constants/live-stream";

import { BOOTSTRAP_COLORS } from "constants/common";

const TEXT_FOR_MUTING = "ORDER_TO_MUTE";

const AGORA_RTC = {
  MODE: {
    live: "live",
    rtc: "rtc",
  },
  CODEC: {
    vp8: "vp8",
    h264: "h264",
  },
  ROLE: {
    host: "host",
    audience: "audience",
  },
  EVENTS: {
    "user-joined": "user-joined",
    "user-left": "user-left",
    "user-published": "user-published",
    "user-unpublished": "user-unpublished",
  },
  MEDIA_TYPE: {
    audio: "audio",
    video: "video",
  },
};

const AGORA_RTM = {
  EVENTS: {
    MessageFromPeer: "MessageFromPeer",
  },
};

const AgoraContext = createContext();
AgoraContext.displayName = "RTCContext";

function AudioPlayer(props) {
  useEffect(() => {
    props.audioTrack && props.audioTrack.play();
  }, [props.audioTrack]);
  return null;
}

function VideoPlayer(props) {
  const videoContainer = useRef(null);

  useEffect(() => {
    if (!videoContainer.current) {
      console.error("Video container not available in DOM");
      return;
    }
    props.videoTrack && props.videoTrack.play(videoContainer.current);
  }, [props.videoTrack]);

  const fullScreenHandle = useFullScreenHandle();

  return (
    <FullScreen handle={fullScreenHandle}>
      <div ref={videoContainer} className="video-container">
        {props.showFullScreenButton ? (
          fullScreenHandle.active ? (
            <Button
              variant="dark"
              className="fullscreen-button"
              onClick={fullScreenHandle.exit}
            >
              <IconContext.Provider
                value={{
                  style: { height: "100%", width: "100%" },
                }}
              >
                <BsFullscreenExit></BsFullscreenExit>
              </IconContext.Provider>
            </Button>
          ) : (
            <Button
              variant="dark"
              className="fullscreen-button"
              onClick={fullScreenHandle.enter}
            >
              <IconContext.Provider
                value={{
                  style: { height: "100%", width: "100%" },
                }}
              >
                <BsFullscreen></BsFullscreen>
              </IconContext.Provider>
            </Button>
          )
        ) : null}
      </div>
    </FullScreen>
  );
}

function UserContainer(props) {
  const rtcContext = useContext(AgoraContext);
  const user = props.user;

  return (
    <>
      {user.hasAudio && user.uid !== rtcContext.rtcClient.uid ? (
        <AudioPlayer audioTrack={user.audioTrack} />
      ) : null}
      {user.hasVideo ? (
        <VideoPlayer
          videoTrack={user.videoTrack}
          showFullScreenButton={props.showFullScreenButton}
        />
      ) : (
        <IconContext.Provider
          value={{
            color: "grey",
            style: { height: "60%", width: "60%" },
          }}
        >
          <FaUserCircle></FaUserCircle>
        </IconContext.Provider>
      )}
    </>
  );
}

function UserList(props) {
  const agoraContext = useContext(AgoraContext);
  const rtcClient = agoraContext.rtcClient;
  const userNames = agoraContext.userNames;
  const teacherUID = agoraContext.agoraParams.teacher_uid;
  const userArray = props.userArray;
  const isLearnDomain = props.domain === "learn";

  function muteUser(uid) {
    agoraContext.sendMessageToPeer(uid, TEXT_FOR_MUTING);
  }

  return userArray.map((user) =>
    user.uid === props.activeUserId ? (
      <div className="d-none" key={user.uid} />
    ) : (
      <Row
        key={user.uid}
        // as="button"
        // onClick={() => props.onUserClick(user.uid)}
        noGutters={true}
        className="h-25 w-100 mb-2 rounded border-dark bg-dark user-container-in-list"
        style={{
          order:
            user.uid === rtcClient.uid ? 1 : user.uid === teacherUID ? 2 : 3,
        }}
      >
        <button
          className="user-container-button-in-list"
          onClick={() => props.onUserClick(user.uid)}
        ></button>
        <Col className="h-100 d-flex align-items-center justify-content-center">
          <UserContainer user={user} showFullScreenButton={false} />
          <p className="user-list-username text-light">
            {user.uid === rtcClient.uid
              ? "You"
              : user.uid === teacherUID
              ? "Instructor"
              : userNames[user.uid] || ANONYMOUS}
          </p>
          <div className="user-list-audio">
            <IconContext.Provider
              value={{
                color: user.hasAudio
                  ? BOOTSTRAP_COLORS.SUCCESS
                  : BOOTSTRAP_COLORS.DANGER,
              }}
            >
              {user.hasAudio ? (
                <FaVolumeUp></FaVolumeUp>
              ) : (
                <BiVolumeMute></BiVolumeMute>
              )}
            </IconContext.Provider>
          </div>
          <div className="user-list-cam">
            <IconContext.Provider
              value={{
                color: user.hasVideo
                  ? BOOTSTRAP_COLORS.SUCCESS
                  : BOOTSTRAP_COLORS.DANGER,
              }}
            >
              {user.hasVideo ? (
                <FaVideo></FaVideo>
              ) : (
                <FaVideoSlash></FaVideoSlash>
              )}
            </IconContext.Provider>
          </div>
          {isLearnDomain ||
          rtcClient.uid !== teacherUID.toString() ||
          rtcClient.uid === user.uid ? null : (
            <Button
              variant="dark"
              className="user-list-mic-control"
              disabled={!user.hasAudio}
              onClick={() => muteUser(user.uid)}
            >
              <IconContext.Provider
                value={{
                  color: BOOTSTRAP_COLORS.MUTED,
                }}
              >
                {user.hasAudio ? (
                  <FaMicrophone></FaMicrophone>
                ) : (
                  <FaMicrophoneSlash></FaMicrophoneSlash>
                )}
              </IconContext.Provider>
            </Button>
          )}
        </Col>
      </Row>
    )
  );
}

function TwoWay(props) {
  const agoraContext = useContext(AgoraContext);
  const agoraParams = agoraContext.agoraParams;
  const teacherUID = agoraParams.teacher_uid;
  const rtmClient = agoraContext.rtmClient;
  const rtcClient = agoraContext.rtcClient;
  const isLearnDomain = props.domain === "learn";

  const [activeUserId, setActiveUserId] = useState(null);
  const [mic, setMic] = useState(false);
  const [cam, setCam] = useState(false);
  const [hideList, setHideList] = useState(false);
  const [showMutedToast, setShowMutedToast] = useState(false);

  let localAudioTrack;
  let localVideoTrack;

  if (rtcClient.localTracks) {
    rtcClient.localTracks.forEach((track) => {
      if (track.trackMediaType === AGORA_RTC.MEDIA_TYPE.audio) {
        localAudioTrack = track;
      } else if (track.trackMediaType === AGORA_RTC.MEDIA_TYPE.video) {
        localVideoTrack = track;
      }
    });
  }

  const selfUser = {
    uid: rtcClient.uid,
    audioTrack: localAudioTrack,
    videoTrack: localVideoTrack,
    hasAudio: mic,
    hasVideo: cam,
  };

  const userArray = rtcClient.remoteUsers.concat(selfUser);

  function onUserClick(userId) {
    setActiveUserId(userId);
  }

  async function onMicClick() {
    if (mic) {
      if (localAudioTrack) {
        rtcClient.unpublish(localAudioTrack);
        localAudioTrack.close();
        console.log("Audio unpublish successful");
      } else {
        console.error("Audio track not found");
      }
    } else {
      localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
      await rtcClient.publish([localAudioTrack]);
      console.log("Audio publish successful");
    }
    // Todo: If permission is not given or other error occurs in setting up track, don't set the mic on
    setMic(!mic);
  }

  async function onCamClick() {
    if (cam) {
      if (localVideoTrack) {
        rtcClient.unpublish(localVideoTrack);
        localVideoTrack.close();
        console.log("Video unpublish successful");
      } else {
        console.error("Video track not found");
      }
    } else {
      localVideoTrack = await AgoraRTC.createCameraVideoTrack();
      await rtcClient.publish([localVideoTrack]);
      console.log("Video publish successful");
    }
    // Todo: If permission is not given or other error occurs in setting up track, don't set the cam on
    setCam(!cam);
  }

  let message;
  let activeUser;
  let avatar;

  if (!activeUserId) {
    message = isLearnDomain
      ? LEARN_DOMAIN_NO_ACTIVE_USER
      : TEACH_DOMAIN_NO_ACTIVE_USER;
    avatar = <FaUserCircle></FaUserCircle>;
    userArray.forEach((user) => {
      if (user.uid === teacherUID.toString()) {
        setActiveUserId(user.uid);
      }
    });
  } else {
    message = USER_WENT_OFFLINE;
    avatar = <FaUserSlash></FaUserSlash>;
    userArray.forEach((user) => {
      if (user.uid === activeUserId) {
        if (!user.hasVideo) {
          message =
            user.uid === rtcClient.uid
              ? YOUR_VIDEO_OFF
              : user.uid === teacherUID.toString()
              ? TEACHER_VIDEO_OFF
              : USER_VIDEO_OFF;
          avatar = <FaUserCircle></FaUserCircle>;
        } else {
          message = null;
          avatar = null;
        }
        activeUser = user;
      }
    });
  }

  // To understand use of useCallback hook here, read either of these -
  // https://stackoverflow.com/a/58165816/5702418
  // https://stackoverflow.com/a/53846698/5702418
  // https://stackoverflow.com/a/62005831/5702418
  const messageListener = useCallback(
    ({ text }, peerId) => {
      if (peerId === teacherUID.toString()) {
        if (text === TEXT_FOR_MUTING) {
          if (mic) {
            if (localAudioTrack) {
              rtcClient.unpublish(localAudioTrack);
              localAudioTrack.close();
            }
            setMic(false);
            setShowMutedToast(true);
          }
        }
      }
    },
    [mic, teacherUID, localAudioTrack]
  );

  useEffect(() => {
    console.log("Setting up RTM listener for TwoWay");
    rtmClient.on(AGORA_RTM.EVENTS["MessageFromPeer"], messageListener);
    return () => {
      rtmClient.off(AGORA_RTM.EVENTS["MessageFromPeer"], messageListener);
    };
  }, [messageListener]);

  return (
    <Row className="text-light bg-dark pl-2 pt-2" style={{ height: "90vh" }}>
      <Toast
        className="info-toast bg-transparent border border-secondary rounded text-light"
        onClose={() => setShowMutedToast(false)}
        show={showMutedToast}
        delay={3000}
        autohide
      >
        <Toast.Body>{EXTERNALLY_MUTED}</Toast.Body>
      </Toast>

      {/* Checking for is_two_way below since TwoWay component is also used for streaming by teacher in one-way classes. In case of one-way, primary dispay is made full screen (col-12) */}

      <Col
        xs={hideList || !agoraParams.is_two_way ? 12 : 9}
        className="primary-user-view h-100 border border-secondary rounded d-flex align-items-center justify-content-center"
      >
        {message ? (
          <Row className="h-75 w-100 justify-content-center align-items-center">
            <Col
              xs={12}
              className="h-75 d-flex justify-content-center align-items-center"
            >
              <IconContext.Provider
                value={{
                  color: "grey",
                  style: { height: "60%", width: "60%" },
                }}
              >
                {avatar}
              </IconContext.Provider>
            </Col>
            <Col className="align-self-start">
              <h6 className="text-center text-secondary"> {message} </h6>
            </Col>
          </Row>
        ) : (
          <UserContainer user={activeUser} showFullScreenButton={true} />
        )}

        {/* Checking for is_two_way below since TwoWay component is also being used for streaming by teacher in one-way classes. In case of one-way, user-list is always hidden, so button to hide list is not needed */}

        {!agoraParams.is_two_way ? null : (
          <Button
            variant="dark"
            className="hide-user-list-button"
            onClick={() => setHideList(!hideList)}
          >
            <IconContext.Provider
              value={{
                style: { height: "100%", width: "100%" },
              }}
            >
              {hideList ? (
                <BsChevronCompactLeft></BsChevronCompactLeft>
              ) : (
                <BsChevronCompactRight></BsChevronCompactRight>
              )}
            </IconContext.Provider>
          </Button>
        )}
        <Button
          variant="dark"
          className={
            "border rounded-circle media-controls microphone " +
            (mic ? "border-success" : "border-danger")
          }
          onClick={onMicClick}
        >
          <IconContext.Provider
            value={{
              color: mic ? BOOTSTRAP_COLORS.SUCCESS : BOOTSTRAP_COLORS.DANGER,
              style: { height: "100%", width: "100%" },
            }}
          >
            {mic ? (
              <FaMicrophone></FaMicrophone>
            ) : (
              <FaMicrophoneSlash></FaMicrophoneSlash>
            )}
          </IconContext.Provider>
        </Button>
        <Button
          variant="dark"
          className={
            "border rounded-circle media-controls camera " +
            (cam ? "border-success" : "border-danger")
          }
          onClick={onCamClick}
        >
          <IconContext.Provider
            value={{
              color: cam ? BOOTSTRAP_COLORS.SUCCESS : BOOTSTRAP_COLORS.DANGER,
              style: { height: "100%", width: "100%" },
            }}
          >
            {cam ? <FaVideo></FaVideo> : <FaVideoSlash></FaVideoSlash>}
          </IconContext.Provider>
        </Button>
      </Col>

      {/* Checking for is_two_way below since TwoWay component is also being used for streaming by teacher in one-way classes. In case of one-way, user-list is hidden */}

      <Col
        xs={3}
        className={
          "h-100 " + (hideList || !agoraParams.is_two_way ? "d-none" : "")
        }
      >
        <Scrollbars style={{ width: "100%", height: "100%" }}>
          {/* <UserContainer
            user={{
              uid: rtcClient.uid,
              audioTrack: localAudioTrack,
              videoTrack: localVideoTrack,
              hasAudio: localAudioTrack ? true : false,
              hasVideo: localVideoTrack ? true : false,
            }}
            order=""
            as="Button"
            onClick={onUserClick}
          /> */}
          <div className="user-list-flex-container">
            <UserList
              userArray={userArray}
              activeUserId={activeUserId}
              onUserClick={onUserClick}
              domain={props.domain}
            />
          </div>
        </Scrollbars>
      </Col>
    </Row>
  );
}

function OneWay() {
  const agoraContext = useContext(AgoraContext);
  const userArray = agoraContext.rtcClient.remoteUsers;
  const teacherUID = agoraContext.agoraParams.teacher_uid;

  let teacher;
  userArray.forEach((user) => {
    if (user.uid === teacherUID.toString()) {
      teacher = user;
    }
  });

  return (
    <Row
      className="justify-content-center align-items-center text-light bg-dark w-90 pt-2"
      style={{ height: "90vh" }}
    >
      <Col className="h-100 d-flex align-items-center justify-content-center">
        {!teacher ? (
          <Row className="h-100 w-100 justify-content-center align-items-center">
            <Col
              xs={12}
              className="h-75 d-flex justify-content-center align-items-center"
            >
              <IconContext.Provider
                value={{
                  color: "grey",
                  style: { height: "60%", width: "60%" },
                }}
              >
                <FaUserCircle></FaUserCircle>
              </IconContext.Provider>
            </Col>
            <Col className="align-self-start">
              <h6 className="text-center text-light">
                {LEARN_DOMAIN_NO_ACTIVE_USER}
              </h6>
            </Col>
          </Row>
        ) : (
          <Row className="h-100 w-100 justify-content-center align-items-center">
            <Col
              xs={12}
              className={
                "d-inline-flex justify-content-center align-items-center " +
                (teacher.hasVideo ? "h-100" : "h-75")
              }
            >
              <UserContainer user={teacher} showFullScreenButton={true} />
            </Col>
            <div className="w-100"></div>
            {teacher.hasVideo ? null : (
              <Col className="align-self-start">
                <h6 className="text-center text-light">{TEACHER_VIDEO_OFF}</h6>
              </Col>
            )}
          </Row>
        )}
      </Col>
    </Row>
  );
}

function LiveStream(props) {
  const appContext = useAppContext();
  const authenticatedUser = appContext.authenticatedUser;
  const [remoteUsersNames, setRemoteUsersNames] = useState({});
  const [rtmJoined, joinRTM] = useState();
  const isLearnDomain = props.domain === "learn";

  const agoraParams = props.location.state;

  const uid = isLearnDomain ? agoraParams.student_uid : agoraParams.teacher_uid;

  async function addRemoteUserName(user) {
    if (user && user.uid) {
      try {
        const attributeMap = await rtmClient.getUserAttributes(
          user.uid.toString()
        );

        setRemoteUsersNames((remoteUsersNames) => ({
          ...remoteUsersNames,
          [user.uid]: attributeMap["username"],
        }));
      } catch (e) {
        console.log("Error in fetching username from rtm: ", e);
      }
    }
  }

  // TODO: check if browser is safari 12.1 or earlier - use codec "h264" then
  // TODO: see if mode should be "rtc" for 2-way streaming
  const [rtcClient] = useState(
    AgoraRTC.createClient({
      mode: AGORA_RTC.MODE.live,
      codec: AGORA_RTC.CODEC.vp8,
      role:
        agoraParams.is_two_way || !isLearnDomain
          ? AGORA_RTC.ROLE.host
          : AGORA_RTC.ROLE.audience,
    })
  );

  const [rtmClient] = useState(AgoraRTM.createInstance(agoraParams.app_id));

  // To force update (https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate)
  const [ignored, forceUpdate] = useReducer((x) => x + 1, 0);

  function onCloseClick() {
    // TODO: any Agora cleanup functions should be called here if they are not already part of component unmount
    navigate(-1);
  }

  function sendMessageToPeer(uid, message) {
    rtmClient
      .sendMessageToPeer({ text: message }, uid.toString())
      .then((sendResult) => {
        if (sendResult.hasPeerReceived) {
          console.log("Peer received");
        } else {
          console.log("Message in black hole");
        }
      })
      .catch((error) => {
        console.log("Message failed with error: " + error);
      });
  }

  // This useEffect is for onComponentMount only - define callbacks and join
  // Use a separate useEffect for componentDidUpdate
  useEffect(() => {
    async function join(rtcClient, rtmClient) {
      await rtmClient.login({
        token: agoraParams.agora_rtm_token,
        uid: uid.toString(),
      });
      console.log("AgoraRTM login successful");
      joinRTM(true);

      await rtcClient.join(
        agoraParams.app_id,
        agoraParams.agora_channel_name,
        agoraParams.agora_rtc_token,
        uid.toString()
      );
      console.log("Channel join successful");
      forceUpdate();
    }

    rtcClient.on(AGORA_RTC.EVENTS["user-joined"], (user) => {
      addRemoteUserName(user);
      forceUpdate();
      console.log("User joined: ", user.uid);
    });

    rtcClient.on(AGORA_RTC.EVENTS["user-left"], (user) => {
      forceUpdate();
      console.log("User left: ", user.uid);
    });

    rtcClient.on(
      AGORA_RTC.EVENTS["user-published"],
      async (user, mediaType) => {
        console.log(`User published ${mediaType}: ${user.uid}`);
        if (mediaType === AGORA_RTC.MEDIA_TYPE.audio) {
          await rtcClient.subscribe(user, AGORA_RTC.MEDIA_TYPE.audio);
          console.log(`Subscribe audio success for user: ${user.uid}`);
        } else if (mediaType === AGORA_RTC.MEDIA_TYPE.video) {
          await rtcClient.subscribe(user, AGORA_RTC.MEDIA_TYPE.video);
          console.log(`Subscribe video success for user: ${user.uid}`);
        }
        forceUpdate();
      }
    );

    rtcClient.on(
      AGORA_RTC.EVENTS["user-unpublished"],
      async (user, mediaType) => {
        forceUpdate();
        console.log("User unpublished: ", user.uid);
      }
    );

    join(rtcClient, rtmClient);

    // willComponentUnmount
    return function cleanup() {
      console.log("Begin cleanup");

      if (rtcClient.localTracks) {
        rtcClient.localTracks.forEach((track) => track.close());
      }

      rtcClient.leave();
      console.log("Channel leave successful");

      if (rtmClient) {
        rtmClient.logout();
        console.log("RTM logout successful");
      }
    };
  }, []);

  useEffect(() => {
    if (authenticatedUser && rtmJoined && rtmClient) {
      const displayName = authenticatedUser.first_name
        ? authenticatedUser.first_name + (authenticatedUser.last_name || "")
        : ANONYMOUS;

      try {
        console.log("Updating user name on RTM: ", displayName);
        rtmClient.addOrUpdateLocalUserAttributes({
          username: displayName,
        });
      } catch (e) {
        console.log("Error in updating user name on RTM: ", e);
      }
    } else {
      console.log("RTM client not found when updating user name");
    }
  }, [rtmJoined, authenticatedUser]);

  useEffect(() => {
    if (authenticatedUser === null) {
      navigate(`/${props.domain}`);
    }
  });

  if (authenticatedUser === false) {
    return (
      <Container>
        <BigSpinner />
      </Container>
    );
  } else if (authenticatedUser === null) {
    return null;
  }

  return (
    <AgoraContext.Provider
      value={{
        rtcClient: rtcClient,
        rtmClient: rtmClient,
        agoraParams: agoraParams,
        ignored: ignored,
        userNames: remoteUsersNames,
        uid: uid,
        sendMessageToPeer: sendMessageToPeer,
      }}
    >
      <Container fluid className="bg-dark rounded live-stream-screen">
        <Button
          className="go-back-button"
          variant="link"
          onClick={onCloseClick}
        >
          <IconContext.Provider
            value={{
              color: "white",
              style: { height: "100%", width: "100%" },
            }}
          >
            <IoIosArrowRoundBack></IoIosArrowRoundBack>
          </IconContext.Provider>
        </Button>
        {/* 
        For two-way streaming by student or teacher - TwoWay component
        For one-way streaming in by student - OneWay component
        For one-way streaming out by teacher - TwoWay component (since it already handles all the logic of camera, mic etc)
        Component can be renamed later to represent the purpose.
        */}
        {agoraParams.is_two_way ? (
          <TwoWay domain={props.domain} />
        ) : isLearnDomain ? (
          <OneWay />
        ) : (
          <TwoWay domain={props.domain} />
        )}
      </Container>
    </AgoraContext.Provider>
  );
}

export default LiveStream;
